import React, { useEffect, useState } from 'react';
import { Star, Search, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Compte → « Mes partenaires favoris » : gérer la liste hors réservation (ajout par recherche, retrait). */
export const FavoritePartnersCard = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => fetch(`${API_URL}/api/tee-times/portal/frequent-partners`, { headers })
    .then(r => (r.ok ? r.json() : { partners: [] })).then(d => setPartners(d.partners || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/tee-times/portal/search-clients?q=${encodeURIComponent(q.trim())}`, { headers })
        .then(r => (r.ok ? r.json() : [])).then(d => setResults((d.clients || d || []).slice(0, 6))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const add = async (c) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/tee-times/portal/partners/favorites`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: c.id }) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Erreur');
      toast.success(`${c.first_name} ${c.last_name} ajouté à vos favoris`);
      setQ(''); setResults([]); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/tee-times/portal/partners/favorites/${p.id}`, { method: 'DELETE', headers });
      toast.success(`${p.first_name} retiré de vos favoris`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const favorites = partners.filter(p => p.favorite);
  const suggested = partners.filter(p => !p.favorite);

  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} data-testid="favorite-partners-card">
      <div className="flex items-center gap-2">
        <Star size={16} className="fill-amber-400 text-amber-500" />
        <p className="text-sm font-semibold text-neutral-800">Mes partenaires favoris</p>
        <span className="ml-auto text-[11px] text-neutral-500">{favorites.length}</span>
      </div>
      <p className="text-[11px] text-neutral-500">Proposés en un clic à chaque réservation de départ.</p>
      {favorites.length === 0 ? (
        <p className="text-xs text-neutral-500 italic" data-testid="favorite-partners-empty">Aucun favori pour l'instant.</p>
      ) : (
        <div className="space-y-1.5">
          {favorites.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-2.5 py-1.5" data-testid={`favorite-partner-${p.id}`}>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-neutral-800 truncate">{p.first_name} {p.last_name}</p>
                <p className="text-[10px] text-neutral-500">{p.group || 'Client'}{p.times ? ` · ${p.times} parties ensemble` : ''}</p>
              </div>
              <button type="button" disabled={busy} onClick={() => remove(p)} className="p-1 rounded hover:bg-neutral-200" title="Retirer" data-testid={`favorite-partner-remove-${p.id}`}><X size={14} className="text-neutral-500" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-2.5 text-neutral-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ajouter un partenaire (nom, prénom)…" className="w-full h-8 pl-7 pr-2 text-sm border rounded-lg" data-testid="favorite-partner-search" />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border bg-white shadow-lg text-sm max-h-48 overflow-auto">
            {results.map(c => (
              <button key={c.id} type="button" disabled={busy || favorites.some(f => f.id === c.id)} onClick={() => add(c)} className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-neutral-50 disabled:opacity-40" data-testid={`favorite-partner-result-${c.id}`}>
                <span>{c.first_name} {c.last_name}</span><span className="text-[10px] text-neutral-500">{c.group || ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {suggested.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-neutral-500 mb-1 flex items-center gap-1"><Users size={11} /> Vous jouez souvent avec</p>
          <div className="flex flex-wrap gap-1">
            {suggested.map(p => (
              <button key={p.id} type="button" disabled={busy} onClick={() => add(p)} className="text-[11px] px-2 py-1 rounded-full bg-neutral-100 hover:bg-amber-100 text-neutral-700 inline-flex items-center gap-1" title="Épingler en favori" data-testid={`favorite-partner-suggest-${p.id}`}>
                <Star size={10} className="text-neutral-400" /> {p.first_name} {p.last_name} <span className="text-neutral-400">×{p.times}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
